import { useEffect, useRef, useState } from 'react';
import { shouldUpdateHubspot, colorList } from '@shared/types/churnRisk';
import useClickOutside from '@/hooks/useClickOutside';
import useClient from '@/hooks/useClient';
import UpdateRiskStatusModal from '@/components/UpdateRiskStatusModal';
import { alertMessageSuccess } from '@/utils/alerts';

interface Props {
  clientId: number;
  colorStatus: string;
}

function SelectColorStatus({ clientId, colorStatus }: Props) {
  const { upsertClientColorStatus } = useClient();
  const [isOpen, setIsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [oldSelectedColor, setOldSelectedColor] = useState<string>('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    setSelectedColor(colorStatus);
    setOldSelectedColor(colorStatus);
  }, [colorStatus]);

  const onSave = async (comment: string, color: string) => {
    const result = await upsertClientColorStatus(clientId, color, comment);
    if (result) {
      setShowModal(false);
      setSelectedColor(color);
      setOldSelectedColor(color);
      alertMessageSuccess('Status Color updated successfully.');
    }

    return result || '';
  };

  const handleColorChange = async (newColor: string) => {
    const prevColor = selectedColor;
    setOldSelectedColor(prevColor);
    setSelectedColor(newColor);

    if (shouldUpdateHubspot(prevColor, newColor)) {
      setIsOpen(false);
      setShowModal(true);
    } else {
      setIsOpen(false);
      setShowModal(false);
      await onSave('No comment', newColor);
    }
  };

  useClickOutside(wrapperRef, () => {
    setIsOpen(false);
  });

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  return (
    <div className={`${isOpen ? 'relative' : ''} inline-block cursor-pointer`}>
      <div
        role='button'
        tabIndex={0}
        id='selector'
        onKeyDown={toggleDropdown}
        className='flex align-center px-2 py-1.5 rounded input-accent  w-full static'
        onClick={toggleDropdown}
        data-testid='client-select-button-status'
      >
        <span className=''>
          {selectedColor ? (
            <div
              className={`w-4 h-4 circle-${selectedColor} rounded-full`}
              data-testid='client-select-status'
            />
          ) : (
            'List of icons'
          )}
        </span>
      </div>
      {isOpen && (
        <div
          ref={wrapperRef}
          className={`absolute left-0 right-0 border-1 border-t-0 border rounded-lg 
            input-accent max-h-[160px] no-scrollbar overflow-y-scroll z-20`}
        >
          {colorList.map((color) => {
            return (
              <div
                role='option'
                className='hover:bg-slate-800 p-2'
                // eslint-disable-next-line react/no-array-index-key
                key={color}
                aria-label='option color'
                aria-selected={color === selectedColor}
                tabIndex={0}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    await handleColorChange(color);
                  }
                }}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={async () => handleColorChange(color)}
              >
                <div className={`w-4 h-4 rounded-full circle-${color}`} />
              </div>
            );
          })}
        </div>
      )}
      {showModal ? (
        <UpdateRiskStatusModal
          isOpen={showModal}
          onSave={onSave}
          color={selectedColor}
          onClose={() => {
            setShowModal(false);
            setSelectedColor(oldSelectedColor);
          }}
        />
      ) : null}
    </div>
  );
}

export default SelectColorStatus;
